import {
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { first, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { Instrument } from '@prlw/core/monitor/all-instruments.entity';
import { AllTableTooltipComponent } from './all-table-tooltip.component';
import { AllTableModifiersService } from '@prlw/core/monitor/all-table-modifiers.service';
import { BasisDetailsInterface } from '../../basis-details.interface';

@Directive({
  selector: '[prlwAllTableTooltip]',
})
export class AllTableTooltipDirective implements OnDestroy {
  @Input('prlwAllTableTooltip')
  public instrument!: Instrument;
  @Input() public basisDetails!: BasisDetailsInterface | null;
  private _overlayRef?: OverlayRef;
  private _tooltipRef!:
    | EmbeddedViewRef<AllTableTooltipComponent>
    | ComponentRef<AllTableTooltipComponent>;
  private readonly _destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly overlay: Overlay,
    private readonly allTableModifiersState: AllTableModifiersService,
  ) {}

  @HostListener('mouseenter')
  public open(): void {
    const offsetY = 25;
    const offsetX = -25;
    const overlayConfig = new OverlayConfig({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this._elementRef.nativeElement)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY,
            offsetX,
          },
        ]),
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
    this._overlayRef = this.overlay.create(overlayConfig);
    const modalPortal = new ComponentPortal(AllTableTooltipComponent);
    this._tooltipRef = this._overlayRef.attach(modalPortal);
    this._tooltipRef.instance.instrument = this.instrument;
    this._tooltipRef.instance.basisDetails = this.basisDetails;
    this.allTableModifiersState.modifier$
      .pipe(
        map((modifier) => modifier.type),
        first(),
        takeUntil(this._destroy$),
      )
      .subscribe(
        (modifierType) =>
          ((
            this._tooltipRef as ComponentRef<AllTableTooltipComponent>
          ).instance.modifierType = modifierType),
      );

    this._overlayRef
      .keydownEvents()
      .pipe(takeUntil(this._destroy$))
      .subscribe((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.close();
        }
      });
  }

  @HostListener('mouseleave')
  public close(): void {
    this._destroy$.next();
    this._overlayRef?.detach();
  }

  public ngOnDestroy(): void {
    this.close();
    this._destroy$.complete();
  }
}
